/**
 * Remembers the last leaderboard render across navigations.
 *
 * WHY
 * ---
 * Opening a product unmounts the leaderboard. Coming back mounts it fresh, so
 * every piece of state starts empty and the page shows skeleton rows, an empty
 * criteria picker and an empty category picker until the request returns. It
 * reads as lag, and it is worse than lag: the filters LOOK cleared, as though
 * the selection had been lost.
 *
 * Holding the last payload in module scope means the return render has the
 * boards, categories and rows immediately. The request still runs and replaces
 * it — this is a cache for the first paint, not a substitute for fresh data.
 *
 * Module scope rather than sessionStorage on purpose: it dies with the tab,
 * carries no serialisation cost on a list of hundreds of products, and can
 * never serve data from a previous session.
 */

import type { LeaderboardPayload } from '@/api/client';
import type { Product } from '@/types/product';

type CombinedEntry = NonNullable<LeaderboardPayload['combined']>[number];

export interface LeaderboardSnapshot {
  key: string;
  payload: LeaderboardPayload;
  products: Product[];
  combined: CombinedEntry[];
  offset: number;
}

let snapshot: LeaderboardSnapshot | null = null;

/** Identifies a query, so a cached render is never shown for a different one. */
export function snapshotKey(
  selected: string[],
  category: string | null,
  audience: string,
): string {
  return `${[...selected].sort().join(',')}|${category ?? ''}|${audience}`;
}

export function readSnapshot(key: string): LeaderboardSnapshot | null {
  return snapshot !== null && snapshot.key === key ? snapshot : null;
}

export function writeSnapshot(next: LeaderboardSnapshot): void {
  snapshot = next;
}
